import shaka from 'shaka-player/dist/shaka-player.ui'
import { VideoPlayer, VideoPlayerConfig, VideoPlayerCallbacks } from './VideoPlayer'
import { getLogger } from './logger'

const log = getLogger('VideoPlayerManager')

export class VideoPlayerManager {
  private static instance: VideoPlayerManager | null = null
  private players: Map<string, VideoPlayer> = new Map()
  private playerCounter = 0
  private _supported: boolean = false

  public init(): void {
    // Install built-in polyfills to patch browser incompatibilities
    shaka.polyfill.installAll()
    log('Polyfills installed')
    this._supported = VideoPlayer.isBrowserSupported()
  }

  public supported(): boolean {
    return this._supported
  }

  get count(): number {
    return this.players.size
  }

  get playerIds(): string[] {
    return Array.from(this.players.keys())
  }

  findPlayerByVideoElement(videoElement: HTMLVideoElement): VideoPlayer | null {
    for (const player of this.players.values()) {
      if (player['videoElement'] === videoElement) {
        return player
      }
    }
    return null
  }

  async createPlayerSafe(
    containerElement: HTMLDivElement,
    config: VideoPlayerConfig,
    callbacks?: VideoPlayerCallbacks
  ): Promise<VideoPlayer> {
    const existingPlayer = this.findPlayerByVideoElement(containerElement.querySelector('video') as HTMLVideoElement)
    if (existingPlayer) {
      log('createPlayerSafe: Video element is already in use by player %s', existingPlayer.id)
      await existingPlayer.waitUntilDestroyed()
    }

    return this.createPlayer(containerElement, config, callbacks)
  }

  async createPlayer(
    containerElement: HTMLDivElement,
    config: VideoPlayerConfig,
    callbacks?: VideoPlayerCallbacks
  ): Promise<VideoPlayer> {
    log('Creating video player, existing: %d', this.players.size)

    // Generate unique ID
    const id = `player-${this.playerCounter}-${Date.now()}`

    try {
      // Create player instance
      const player = new VideoPlayer(id, containerElement, config, {
        ...callbacks,
        onDestroyed: () => {
          // Remove from manager when destroyed
          this.players.delete(id)
        },
      })

      this.playerCounter++ // Initialize and load
      await player.initialize()
      await player.load()

      this.players.set(id, player)

      log('Created player %s', id)

      return player
    } catch (error) {
      log(`VideoPlayerManager: Failed to create player ${id}:`, error)
      // Clean up if creation failed
      this.players.delete(id)
      throw error
    }
  }

  getPlayer(id: string): VideoPlayer | undefined {
    return this.players.get(id)
  }

  async destroyPlayer(id: string): Promise<boolean> {
    const player = this.players.get(id)
    if (!player) {
      log(`VideoPlayerManager: Player ${id} not found`)
      return false
    }

    this.players.delete(id)

    try {
      await player.destroy()
      log('Destroyed player %s', id)
      log('Remaining players: %d', this.count)
      return true
    } catch (error) {
      log(`VideoPlayerManager: Error destroying player ${id}: ${error}`)
    }
    return false
  }

  static getInstance(): VideoPlayerManager {
    if (!VideoPlayerManager.instance) {
      VideoPlayerManager.instance = new VideoPlayerManager()
      VideoPlayerManager.instance.init()
    }
    return VideoPlayerManager.instance
  }
}
